import api from './client';
import type { OverviewStats, DailyStats, CountryStats, UserActivityStats, AnomalyAlert, SystemStats, SystemMetricsHistory, SystemMetricsSample } from '@/types';

// Transform snake_case to camelCase for stats
function transformOverviewStats(data: Record<string, unknown>): OverviewStats {
  return {
    totalUsers: data.total_users as number,
    totalBlahblahs: data.total_blahblahs as number,
    totalPlays: data.total_plays as number,
    totalLikes: data.total_likes as number,
    totalFollows: data.total_follows as number,
    totalReports: data.total_reports as number,
    usersToday: data.users_today as number,
    blahblahsToday: data.blahblahs_today as number,
    activeUsersToday: data.active_users_today as number,
    uniqueCountries: data.unique_countries as number,
  };
}

function transformDailyStats(data: Record<string, unknown>): DailyStats {
  return {
    date: data.date as string,
    newUsers: data.new_users as number,
    activeUsers: data.active_users as number,
    newBlahblahs: data.new_blahblahs as number,
    totalPlays: data.total_plays as number,
    totalLikes: data.total_likes as number,
    totalFollows: data.total_follows as number,
    totalReports: data.total_reports as number,
    totalReplies: data.total_replies as number,
    uniqueCountries: data.unique_countries as number,
  };
}

function transformCountryStats(data: Record<string, unknown>): CountryStats {
  return {
    countryCode: data.country_code as string,
    userCount: data.user_count as number,
    sessionCount: data.session_count as number,
  };
}

function transformUserActivityStats(data: Record<string, unknown>): UserActivityStats {
  return {
    userId: data.user_id as string,
    displayName: data.display_name as string,
    email: data.email as string | null,
    blahblahCount: data.blahblah_count as number,
    totalLikesReceived: data.total_likes_received as number,
    totalViewsReceived: data.total_views_received as number,
    followerCount: data.follower_count as number,
    lastActive: data.last_active as string | null,
    createdAt: data.created_at as string,
  };
}

function transformAnomalyAlert(data: Record<string, unknown>): AnomalyAlert {
  return {
    id: data.id as string,
    alertType: data.alert_type as string,
    severity: data.severity as string,
    message: data.message as string,
    metricValue: data.metric_value as number | null,
    thresholdValue: data.threshold_value as number | null,
    emailSent: data.email_sent as boolean,
    acknowledged: data.acknowledged as boolean,
    acknowledgedBy: data.acknowledged_by as string | null,
    acknowledgedAt: data.acknowledged_at as string | null,
    createdAt: data.created_at as string,
  };
}

export async function getOverviewStats(): Promise<OverviewStats> {
  const { data } = await api.get('/admin/stats/overview');
  return transformOverviewStats(data);
}

export async function getDailyStats(params?: { from?: string; to?: string; limit?: number }): Promise<DailyStats[]> {
  const { data } = await api.get('/admin/stats/daily', { params });
  return data.map(transformDailyStats);
}

export async function refreshDailyStats(date?: string): Promise<DailyStats> {
  const { data } = await api.post('/admin/stats/daily/refresh', null, { params: { from: date } });
  return transformDailyStats(data);
}

export async function getCountryStats(limit?: number): Promise<CountryStats[]> {
  const { data } = await api.get('/admin/stats/countries', { params: { limit } });
  return data.map(transformCountryStats);
}

export async function getTopUsers(limit?: number): Promise<UserActivityStats[]> {
  const { data } = await api.get('/admin/stats/users', { params: { limit } });
  return data.map(transformUserActivityStats);
}

export async function deleteStatsPeriod(from: string, to: string): Promise<{ deletedStatsRows: number; deletedSessionRows: number }> {
  const { data } = await api.delete('/admin/stats/period', { data: { from, to } });
  return {
    deletedStatsRows: data.deleted_stats_rows,
    deletedSessionRows: data.deleted_session_rows,
  };
}

export async function getAlerts(limit?: number): Promise<AnomalyAlert[]> {
  const { data } = await api.get('/admin/alerts', { params: { limit } });
  return data.map(transformAnomalyAlert);
}

export async function acknowledgeAlert(alertId: string): Promise<AnomalyAlert> {
  const { data } = await api.post(`/admin/alerts/${alertId}/acknowledge`);
  return transformAnomalyAlert(data);
}

export async function getAdminActions(limit?: number): Promise<unknown[]> {
  const { data } = await api.get('/admin/actions', { params: { limit } });
  return data;
}

function transformSystemStats(data: Record<string, unknown>): SystemStats {
  return {
    cpuUsagePercent: data.cpu_usage_percent as number,
    cpuCount: data.cpu_count as number,
    memoryTotalBytes: data.memory_total_bytes as number,
    memoryUsedBytes: data.memory_used_bytes as number,
    memoryUsagePercent: data.memory_usage_percent as number,
    diskTotalBytes: data.disk_total_bytes as number,
    diskUsedBytes: data.disk_used_bytes as number,
    diskUsagePercent: data.disk_usage_percent as number,
    dbPoolSize: data.db_pool_size as number,
    dbPoolIdle: data.db_pool_idle as number,
    dbPoolActive: data.db_pool_active as number,
    uptimeSeconds: data.uptime_seconds as number,
    totalRequests: data.total_requests as number,
    version: data.version as string,
  };
}

function transformMetricsSample(data: Record<string, unknown>): SystemMetricsSample {
  return {
    sampledAt: data.sampled_at as string,
    cpuUsagePercent: data.cpu_usage_percent as number,
    memoryTotalBytes: data.memory_total_bytes as number,
    memoryUsedBytes: data.memory_used_bytes as number,
    memoryUsagePercent: data.memory_usage_percent as number,
    diskTotalBytes: data.disk_total_bytes as number,
    diskUsedBytes: data.disk_used_bytes as number,
    diskUsagePercent: data.disk_usage_percent as number,
    dbPoolSize: data.db_pool_size as number,
    dbPoolIdle: data.db_pool_idle as number,
    dbPoolActive: data.db_pool_active as number,
    uptimeSeconds: data.uptime_seconds as number,
    requestCount: data.request_count as number,
  };
}

export async function getSystemStats(): Promise<SystemStats> {
  const { data } = await api.get('/admin/stats/system');
  return transformSystemStats(data);
}

export async function getSystemMetricsHistory(hours?: number): Promise<SystemMetricsHistory> {
  const { data } = await api.get('/admin/stats/system/history', { params: { hours } });
  return {
    rangeHours: data.range_hours as number,
    intervalSeconds: data.interval_seconds as number,
    sampleCount: data.sample_count as number,
    samples: (data.samples as Record<string, unknown>[]).map(transformMetricsSample),
  };
}
